import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Tooltip,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  TableContainer as MuiTableContainer,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { BarChart3, Download } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Badge } from "./ui/badge";
import { ArrowUpDown, ArrowUp, ArrowDown, Search, Eye } from "lucide-react";

import "./Statistics.css";
import { getBackendUrl } from "../config/env";

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884D8",
  "#82CA9D",
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`statistics-tabpanel-${index}`}
      aria-labelledby={`statistics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Statistics: React.FC = () => {
  const navigate = useNavigate();
  const { user, centerInfo, selectedCenterId } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [surveys, setSurveys] = useState<any[]>([]);

  const [selectedSurvey, setSelectedSurvey] = useState<string>("all");
  const [dateRange, setDateRange] = useState<string>("all");
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");
  const [statisticsData, setStatisticsData] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc' | null;
  }>({ key: null, direction: null });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [summaryData, setSummaryData] = useState({
    totalSurveys: 0,
    totalResponses: 0,
    averageCompletionRate: 0,
    averageResponseTime: "0분",
    mostPopularSurvey: "",
    responseGrowthRate: 0,
  });

  // 날짜 범위 계산 함수
  const getDateRange = useCallback(() => {
    const now = new Date();
    let startDate: Date | null = null;
    let endDate: Date | null = null;

    switch (dateRange) {
      case "today":
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endDate = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          23,
          59,
          59
        );
        break;
      case "week":
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      case "month":
        startDate = new Date(
          now.getFullYear(),
          now.getMonth() - 1,
          now.getDate()
        );
        endDate = now;
        break;
      case "year":
        startDate = new Date(
          now.getFullYear() - 1,
          now.getMonth(),
          now.getDate()
        );
        endDate = now;
        break;
      case "custom":
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59); // 종료일 끝까지 포함
        }
        break;
      default: // 'all'
        return { startDate: null, endDate: null };
    }

    return { startDate, endDate };
  }, [dateRange, customStartDate, customEndDate]);

  // 날짜로 응답 필터링 함수
  const filterResponsesByDate = useCallback((responses: any) => {
    // responses가 배열인지 확인
    if (!Array.isArray(responses)) {
      console.warn("filterResponsesByDate: responses is not an array", responses);
      return [];
    }

    const { startDate, endDate } = getDateRange();

    if (!startDate || !endDate) {
      return responses; // 전체 기간
    }

    return responses.filter((response: any) => {
      const responseDate = new Date(
        response.publishedAt || response.submittedAt || response.completedAt || response.createdAt
      );
      return responseDate >= startDate && responseDate <= endDate;
    });
  }, [getDateRange]);

  const loadStatisticsData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const centerUserId =
        selectedCenterId || user?.centerId || centerInfo?.centerId;

      // 설문조사 목록 가져오기 (통계용으로 모든 데이터 필요)
      const params = new URLSearchParams({
        page: "1",
        limit: "1000", // 충분히 큰 수로 설정하여 모든 설문조사 가져오기
      });
      
      if (centerUserId) {
        params.append("centerUserId", centerUserId);
      }

      const surveysUrl = `${getBackendUrl()}/api/surveys?${params.toString()}`;
      
      console.log("🔍 Statistics API 요청:", surveysUrl);

      const surveysResponse = await fetch(surveysUrl);
      if (surveysResponse.ok) {
        const surveysData = await surveysResponse.json();
        
        console.log("📊 받은 설문조사 데이터:", surveysData);
        
        // 페이징 응답과 비-페이징 응답 모두 처리
        const surveys = surveysData.data ? surveysData.data : surveysData;
        console.log("📋 처리된 설문조사 목록:", surveys);
        
        setSurveys(surveys);

        // 각 설문의 응답 데이터 가져오기
        const allResponsesData: any[] = [];
        const statisticsArray: any[] = [];

        for (const survey of surveys) {
          const surveyId = survey.id || survey._id;

          // 선택된 설문만 또는 전체
          if (selectedSurvey !== "all" && surveyId !== selectedSurvey) {
            continue;
          }

          // 모든 응답을 가져오기 위해 limit을 크게 설정
          const responseParams = new URLSearchParams({
            limit: "1000", // 충분히 큰 수로 설정하여 모든 응답 가져오기
          });
          
          if (centerUserId) {
            responseParams.append("centerUserId", centerUserId);
          }
          
          const responseUrl = `${getBackendUrl()}/api/responses/survey/${surveyId}?${responseParams.toString()}`;

          const responseData = await fetch(responseUrl);
          if (responseData.ok) {
            const responseJson = await responseData.json();
            // API가 {data: [...]} 형식으로 응답하는 경우와 배열로 응답하는 경우 모두 처리
            const responses = Array.isArray(responseJson) ? responseJson : (responseJson.data || []);

            // 날짜 필터링
            const filteredResponses = filterResponsesByDate(responses);
            // 배열인지 확인 후 spread
            if (Array.isArray(filteredResponses)) {
              allResponsesData.push(...filteredResponses);
            }

            // 통계 계산
            const completedResponses = Array.isArray(filteredResponses) 
              ? filteredResponses.filter((r: any) => r.status === "completed")
              : [];
            const totalTime = completedResponses.reduce(
              (sum: number, r: any) => {
                if (r.startedAt && r.completedAt) {
                  const start = new Date(r.startedAt).getTime();
                  const end = new Date(r.completedAt).getTime();
                  return sum + (end - start);
                }
                return sum;
              },
              0
            );

            const avgTime =
              completedResponses.length > 0
                ? Math.floor(totalTime / completedResponses.length / 60000)
                : 0;

            // 질문별 통계 계산
            const questionStats = calculateQuestionStats(
              survey,
              Array.isArray(filteredResponses) ? filteredResponses : []
            );

            const totalResponseCount = Array.isArray(filteredResponses) ? filteredResponses.length : 0;

            statisticsArray.push({
              id: surveyId,
              surveyTitle: survey.title,
              totalResponses: totalResponseCount,
              completedResponses: completedResponses.length,
              completionRate:
                totalResponseCount > 0
                  ? Math.round(
                      (completedResponses.length / totalResponseCount) *
                        100
                    )
                  : 0,
              averageTime: `${avgTime}분`,
              status:
                survey.status === "published" || survey.isPublished
                  ? "게시됨"
                  : "초안",
              createdAt: survey.publishedAt || survey.createdAt,
              questionCount: survey.questions?.length || 0,
              questionStats: questionStats,
            });
          }
        }


        setStatisticsData(statisticsArray);

        // 요약 데이터 계산
        calculateSummaryData(surveys, allResponsesData, statisticsArray);
      }
    } catch (error) {
      console.error("Error loading statistics:", error);
      setError(error instanceof Error ? error.message : "통계 데이터를 불러오는 중 오류가 발생했습니다.");
      // 오류 시 안전한 초기값 설정
      setSurveys([]);
      setStatisticsData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCenterId, user?.centerId, centerInfo?.centerId, selectedSurvey, filterResponsesByDate]);

  useEffect(() => {
    loadStatisticsData();
  }, [loadStatisticsData]);

  const calculateQuestionStats = (survey: any, responses: any) => {
    const questionStats: any[] = [];
    
    // responses가 배열이 아니면 빈 배열 반환
    if (!Array.isArray(responses)) {
      return questionStats;
    }

    survey.questions?.forEach((question: any) => {
      const questionResponses = responses
        .map((r) => r.answers?.find((a: any) => a.questionId === question.id))
        .filter(Boolean)
        .map((a: any) => a.value);

      let stats: any = {
        questionId: question.id,
        question: question.question,
        type: question.type,
        responseCount: questionResponses.length,
        responseRate:
          responses.length > 0
            ? Math.round((questionResponses.length / responses.length) * 100)
            : 0,
      };

      // 타입별 추가 통계
      if (question.type === "rating") {
        const ratings = questionResponses.filter((r) => typeof r === "number");
        const avgRating =
          ratings.length > 0
            ? (ratings.reduce((sum, r) => sum + r, 0) / ratings.length).toFixed(
                2
              )
            : "0";
        stats.averageRating = avgRating;
      } else if (
        question.type === "multiple-choice" ||
        question.type === "dropdown"
      ) {
        const optionCounts: { [key: string]: number } = {};
        question.options?.forEach((opt: string) => {
          optionCounts[opt] = questionResponses.filter((r) => r === opt).length;
        });
        const mostSelected = Object.entries(optionCounts).sort(
          ([, a], [, b]) => b - a
        )[0];
        stats.mostSelected = mostSelected
          ? `${mostSelected[0]} (${mostSelected[1]}명)`
          : "-";
      }

      questionStats.push(stats);
    });

    return questionStats;
  };

  const calculateSummaryData = (
    surveys: any[],
    responses: any[],
    statistics: any[]
  ) => {
    // 배열 확인
    const safeSurveys = Array.isArray(surveys) ? surveys : [];
    const safeResponses = Array.isArray(responses) ? responses : [];
    const safeStatistics = Array.isArray(statistics) ? statistics : [];
    
    const totalResponses = safeResponses.length;
    const completedResponses = safeResponses.filter(
      (r) => r.status === "completed"
    ).length;
    const averageCompletionRate =
      totalResponses > 0
        ? Math.round((completedResponses / totalResponses) * 100)
        : 0;

    // 평균 응답 시간
    const responsesWithTime = safeResponses.filter(
      (r) => r.startedAt && r.completedAt
    );
    let averageTime = "0분";
    if (responsesWithTime.length > 0) {
      const totalTime = responsesWithTime.reduce((sum, r) => {
        const start = new Date(r.startedAt).getTime();
        const end = new Date(r.completedAt).getTime();
        return sum + (end - start);
      }, 0);
      const avgMinutes = Math.floor(
        totalTime / responsesWithTime.length / 60000
      );
      averageTime = `${avgMinutes}분`;
    }

    // 가장 인기있는 설문
    const mostPopular = safeStatistics.length > 0 
      ? [...safeStatistics].sort((a, b) => b.totalResponses - a.totalResponses)[0]
      : null;

    // 응답 증가율 (지난 주 대비)
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const recentResponses = safeResponses.filter(
      (r) => new Date(r.completedAt || r.createdAt) >= oneWeekAgo
    ).length;
    const previousWeekResponses = safeResponses.filter((r) => {
      const date = new Date(r.completedAt || r.createdAt);
      const twoWeeksAgo = new Date();
      twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
      return date < oneWeekAgo && date >= twoWeeksAgo;
    }).length;

    const growthRate =
      previousWeekResponses > 0
        ? Math.round(
            ((recentResponses - previousWeekResponses) /
              previousWeekResponses) *
              100
          )
        : 0;

    setSummaryData({
      totalSurveys: safeSurveys.length,
      totalResponses,
      averageCompletionRate,
      averageResponseTime: averageTime,
      mostPopularSurvey: mostPopular?.surveyTitle || "-",
      responseGrowthRate: growthRate,
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // 정렬 함수
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // 데이터 필터링 및 정렬
  const getFilteredAndSortedData = () => {
    let filteredData = statisticsData;
    
    // 검색 필터링
    if (searchTerm) {
      filteredData = filteredData.filter((item) =>
        item.surveyTitle.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // 정렬
    if (sortConfig.key && sortConfig.direction) {
      filteredData = [...filteredData].sort((a, b) => {
        const aValue = a[sortConfig.key!];
        const bValue = b[sortConfig.key!];
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filteredData;
  };

  // 페이지네이션
  const getPaginatedData = () => {
    const filteredData = getFilteredAndSortedData();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(getFilteredAndSortedData().length / itemsPerPage);

  const exportToExcel = () => {
    const csvContent = generateCSV();
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `통계_${Date.now()}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const generateCSV = () => {
    const headers = [
      "설문 제목",
      "총 응답",
      "완료 응답",
      "완료율(%)",
      "평균 시간",
      "상태",
      "질문 수",
    ];
    const rows = statisticsData.map((stat) => [
      stat.surveyTitle,
      stat.totalResponses,
      stat.completedResponses,
      stat.completionRate,
      stat.averageTime,
      stat.status,
      stat.questionCount,
    ]);

    return [
      headers.join(","),
      ...rows.map((row) => row.map((field) => `"${field}"`).join(",")),
    ].join("\n");
  };

  const getSortIcon = (column: string) => {
    if (sortConfig.key !== column) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="ml-2 h-4 w-4" />
      : <ArrowDown className="ml-2 h-4 w-4" />;
  };

  // 차트 데이터 준비
  const completionRateData = statisticsData.map((stat) => ({
    name:
      stat.surveyTitle.length > 20
        ? stat.surveyTitle.substring(0, 20) + "..."
        : stat.surveyTitle,
    value: stat.completionRate,
  }));

  const responseDistributionData = statisticsData.map((stat) => ({
    name:
      stat.surveyTitle.length > 20
        ? stat.surveyTitle.substring(0, 20) + "..."
        : stat.surveyTitle,
    responses: stat.totalResponses,
    completed: stat.completedResponses,
  }));

  return (
    <div className="max-w-7xl mx-auto p-8">
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <div className="survey-list-header">
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <BarChart3 size={32} />
            통계 대시보드
          </h1>
          <p className="page-description">
            설문조사 통계와 응답 분석을 확인하세요
          </p>
        </div>
        <div className="flex gap-4">
          <Select value={selectedSurvey} onValueChange={setSelectedSurvey}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="설문 선택" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">전체 설문</SelectItem>
              {Array.isArray(surveys) && surveys.map((survey) => (
                <SelectItem
                  key={survey.id || survey._id}
                  value={survey.id || survey._id}
                >
                  {survey.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="기간" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">전체 기간</SelectItem>
              <SelectItem value="today">오늘</SelectItem>
              <SelectItem value="week">최근 1주</SelectItem>
              <SelectItem value="month">최근 1달</SelectItem>
              <SelectItem value="year">최근 1년</SelectItem>
              <SelectItem value="custom">직접입력</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={exportToExcel}
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <Download size={20} />
            엑셀 다운로드
          </Button>
        </div>
      </div>

      {/* 직접입력 날짜 선택 */}
      {dateRange === "custom" && (
        <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <div className="flex gap-4 items-center">
            <div className="flex flex-col gap-2">
              <Label htmlFor="start-date">시작일자</Label>
              <Input
                id="start-date"
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="w-[180px]"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="end-date">종료일자</Label>
              <Input
                id="end-date"
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="w-[180px]"
              />
            </div>
            <Button
              onClick={() => {
                if (!customStartDate || !customEndDate) {
                  alert("시작일자와 종료일자를 모두 선택해주세요.");
                  return;
                }
                if (new Date(customStartDate) > new Date(customEndDate)) {
                  alert("시작일자는 종료일자보다 이전이어야 합니다.");
                  return;
                }
                // useEffect에서 자동으로 업데이트되므로 별도 호출 불필요
                alert("날짜 범위가 적용되었습니다.");
              }}
              className="mt-6"
              disabled={!customStartDate || !customEndDate}
            >
              적용
            </Button>
          </div>
        </div>
      )}

      {/* 요약 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">총 설문 수</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-blue-600">
              {summaryData.totalSurveys}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">총 응답 수</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-green-600">
              {summaryData.totalResponses}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">평균 완료율</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-cyan-600">
              {summaryData.averageCompletionRate}%
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">
              평균 응답 시간
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-orange-600">
              {summaryData.averageResponseTime}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">인기 설문</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold">
              {summaryData.mostPopularSurvey}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">주간 증가율</CardTitle>
          </CardHeader>
          <CardContent>
            <p
              className={`text-3xl font-bold ${
                summaryData.responseGrowthRate >= 0
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {summaryData.responseGrowthRate > 0 ? "+" : ""}
              {summaryData.responseGrowthRate}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* 탭 */}
      <Paper elevation={3}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="설문별 통계" />
          <Tab label="질문별 분석" />
          <Tab label="시각화 차트" />
        </Tabs>

        {/* 설문별 통계 탭 */}
        <TabPanel value={tabValue} index={0}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          ) : (
            <div className="space-y-4">
              {/* 검색 바 */}
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center gap-2 flex-1 max-w-md">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="설문 제목으로 검색..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1"
                  />
                </div>
                <div className="text-sm text-muted-foreground">
                  총 {getFilteredAndSortedData().length}개 설문
                </div>
              </div>

              {/* 테이블 */}
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleSort('surveyTitle')}
                      >
                        <div className="flex items-center">
                          설문 제목
                          {getSortIcon('surveyTitle')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-center cursor-pointer hover:bg-muted/50"
                        onClick={() => handleSort('totalResponses')}
                      >
                        <div className="flex items-center justify-center">
                          총 응답
                          {getSortIcon('totalResponses')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-center cursor-pointer hover:bg-muted/50"
                        onClick={() => handleSort('completedResponses')}
                      >
                        <div className="flex items-center justify-center">
                          완료 응답
                          {getSortIcon('completedResponses')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-center cursor-pointer hover:bg-muted/50"
                        onClick={() => handleSort('completionRate')}
                      >
                        <div className="flex items-center justify-center">
                          완료율
                          {getSortIcon('completionRate')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-center cursor-pointer hover:bg-muted/50"
                        onClick={() => handleSort('averageTime')}
                      >
                        <div className="flex items-center justify-center">
                          평균 시간
                          {getSortIcon('averageTime')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="text-center cursor-pointer hover:bg-muted/50"
                        onClick={() => handleSort('questionCount')}
                      >
                        <div className="flex items-center justify-center">
                          질문 수
                          {getSortIcon('questionCount')}
                        </div>
                      </TableHead>
                      <TableHead className="text-center">상태</TableHead>
                      <TableHead className="text-center">상세보기</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getPaginatedData().length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          {searchTerm ? '검색 결과가 없습니다.' : '데이터가 없습니다.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      getPaginatedData().map((stat) => (
                        <TableRow key={stat.id} className="hover:bg-muted/50">
                          <TableCell>
                            <button
                              className="text-left hover:text-primary hover:underline transition-colors"
                              onClick={() => navigate(`/analytics/${stat.id}`)}
                            >
                              {stat.surveyTitle}
                            </button>
                          </TableCell>
                          <TableCell className="text-center">
                            {stat.totalResponses}
                          </TableCell>
                          <TableCell className="text-center">
                            {stat.completedResponses}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge
                              variant={
                                stat.completionRate >= 80
                                  ? "default"
                                  : stat.completionRate >= 50
                                  ? "secondary"
                                  : "destructive"
                              }
                              className={
                                stat.completionRate >= 80
                                  ? "bg-green-500 hover:bg-green-600"
                                  : stat.completionRate >= 50
                                  ? "bg-yellow-500 hover:bg-yellow-600"
                                  : ""
                              }
                            >
                              {stat.completionRate}%
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {stat.averageTime}
                          </TableCell>
                          <TableCell className="text-center">
                            {stat.questionCount}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge
                              variant={stat.status === "게시됨" ? "default" : "outline"}
                              className={stat.status === "게시됨" ? "bg-green-500 hover:bg-green-600" : ""}
                            >
                              {stat.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/analytics/${stat.id}`)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              분석 보기
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* 페이지네이션 */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    총 {getFilteredAndSortedData().length}개 중 {(currentPage - 1) * itemsPerPage + 1}-
                    {Math.min(currentPage * itemsPerPage, getFilteredAndSortedData().length)}개 표시
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      이전
                    </Button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      다음
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </TabPanel>

        {/* 질문별 분석 탭 */}
        <TabPanel value={tabValue} index={1}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : statisticsData.length === 0 ? (
            <Alert severity="info">선택된 기간에 데이터가 없습니다.</Alert>
          ) : (
            <Box>
              {statisticsData.map((survey) => (
                <Box key={survey.id} sx={{ mb: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    {survey.surveyTitle}
                  </Typography>
                  <MuiTableContainer component={Paper} variant="outlined">
                    <MuiTable size="small">
                      <MuiTableHead>
                        <MuiTableRow>
                          <MuiTableCell>질문</MuiTableCell>
                          <MuiTableCell align="center">타입</MuiTableCell>
                          <MuiTableCell align="center">응답 수</MuiTableCell>
                          <MuiTableCell align="center">응답률</MuiTableCell>
                          <MuiTableCell align="center">추가 정보</MuiTableCell>
                        </MuiTableRow>
                      </MuiTableHead>
                      <MuiTableBody>
                        {survey.questionStats?.map(
                          (stat: any, index: number) => (
                            <MuiTableRow key={stat.questionId}>
                              <MuiTableCell>
                                {index + 1}. {stat.question}
                              </MuiTableCell>
                              <MuiTableCell align="center">
                                <Chip
                                  label={stat.type}
                                  size="small"
                                  variant="outlined"
                                />
                              </MuiTableCell>
                              <MuiTableCell align="center">
                                {stat.responseCount}
                              </MuiTableCell>
                              <MuiTableCell align="center">
                                {stat.responseRate}%
                              </MuiTableCell>
                              <MuiTableCell align="center">
                                {stat.averageRating &&
                                  `평균: ${stat.averageRating}`}
                                {stat.mostSelected &&
                                  `최다: ${stat.mostSelected}`}
                              </MuiTableCell>
                            </MuiTableRow>
                          )
                        )}
                      </MuiTableBody>
                    </MuiTable>
                  </MuiTableContainer>
                </Box>
              ))}
            </Box>
          )}
        </TabPanel>

        {/* 시각화 차트 탭 */}
        <TabPanel value={tabValue} index={2}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                완료율 비교
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={completionRateData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                  />
                  <YAxis />
                  <ChartTooltip />
                  <Bar dataKey="value" fill="#8884d8">
                    {completionRateData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                응답 분포
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={responseDistributionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                  />
                  <YAxis />
                  <ChartTooltip />
                  <Legend />
                  <Bar dataKey="responses" fill="#82ca9d" name="총 응답" />
                  <Bar dataKey="completed" fill="#8884d8" name="완료 응답" />
                </BarChart>
              </ResponsiveContainer>

              <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                설문별 응답 비율
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={statisticsData
                      .filter((stat) => stat.totalResponses > 0) // 0인 항목 필터링
                      .map((stat) => ({
                        name: stat.surveyTitle.length > 30 
                          ? stat.surveyTitle.substring(0, 30) + "..." 
                          : stat.surveyTitle,
                        value: stat.totalResponses,
                        fullName: stat.surveyTitle, // 툴팁용 전체 이름
                      }))}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => {
                      // 값이 전체의 5% 이상인 경우만 라벨 표시
                      if (!entry.value) return null;
                      const total = statisticsData.reduce((sum, stat) => sum + stat.totalResponses, 0);
                      const percentage = (entry.value / total) * 100;
                      if (percentage >= 5) {
                        return `${entry.value}`;
                      }
                      return null;
                    }}
                    outerRadius={150}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statisticsData
                      .filter((stat) => stat.totalResponses > 0)
                      .map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                  </Pie>
                  <ChartTooltip 
                    formatter={(value: any, name: any, props: any) => {
                      return [`${value}개 응답`, props.payload.fullName || name];
                    }}
                  />
                  <Legend 
                    verticalAlign="middle" 
                    align="right"
                    layout="vertical"
                    wrapperStyle={{ paddingLeft: "20px" }}
                    formatter={(value: string) => {
                      const stat = statisticsData.find(s => 
                        s.surveyTitle === value || 
                        (value.includes("...") && s.surveyTitle.startsWith(value.substring(0, 30)))
                      );
                      return stat ? `${value} (${stat.totalResponses})` : value;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          )}
        </TabPanel>
      </Paper>
    </div>
  );
};

export default Statistics;
